import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, throwError } from 'rxjs';
import { environment } from '../../environment/environment';
import {
  InvoiceBackendListModel,
  InvoiceBackendModel,
  InvoiceListModel,
  InvoiceModel,
} from '../models/invoice.model';
import { CouponService } from './coupon.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class InvoiceService {
  constructor(
    private http: HttpClient,
    private userService: UserService,
    private couponService: CouponService
  ) {}

  public getAllInvoices() {
    const formData = new FormData();
    formData.append('mode', 'getAllInvoices');

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: InvoiceBackendListModel[];
      }>(environment.authority_short + 'invoice.php', formData)
      .pipe(
        map(response =>
          response.data.map(booking => this.parseBackendListModel(booking))
        ),
        catchError(() => {
          return throwError(() => new Error('Invoice fetch failed'));
        })
      );
  }

  public getInvoiceById(id: number) {
    const formData = new FormData();
    formData.append('mode', 'getInvoiceById');
    formData.append('id', id.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: InvoiceBackendModel;
      }>(environment.authority_short + 'invoice.php', formData)
      .pipe(
        map(response => this.parseBackendModel(response.data)),
        catchError(() => {
          return throwError(() => new Error('Invoice fetch failed'));
        })
      );
  }

  public downloadInvoice(id: number) {
    const formData = new FormData();
    formData.append('mode', 'downloadInvoice');
    formData.append('id', id.toString());

    return this.http
      .post(environment.authority_short + 'invoice.php', formData, {
        responseType: 'blob',
      })
      .pipe(
        catchError(() => {
          return throwError(() => new Error('Invoice download failed'));
        })
      );
  }

  public confirmPayment(id: number) {
    const formData = new FormData();
    formData.append('mode', 'confirmPayment');
    formData.append('id', id.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'invoice.php', formData)
      .pipe(
        catchError(() => {
          return throwError(() => new Error('Confirm invoice payment failed'));
        })
      );
  }

  public exportInvoicesZip(year?: number) {
    const formData = new FormData();
    formData.append('mode', 'exportInvoicesZip');
    if (year) {
      formData.append('year', year.toString());
    }

    return this.http
      .post(environment.authority_short + 'invoice.php', formData, {
        responseType: 'blob',
      })
      .pipe(
        map(blob => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          const filename = `Rechnungen_${year || new Date().getFullYear()}.zip`;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          return blob;
        }),
        catchError(() => {
          return throwError(() => new Error('Export failed'));
        })
      );
  }

  public parseBackendListModel(
    invoice: InvoiceBackendListModel
  ): InvoiceListModel {
    return {
      id: invoice.id,
      email: invoice.email,
      type: invoice.type,
      creator:
        invoice.creator &&
        this.userService.parseBackendListModel(invoice.creator),
      seminarTitle: invoice.seminar_title,
      invoiceNr: invoice.invoice_nr,
      paymentStatus: invoice.payment_status,
      generatedAt: invoice.generated_at && new Date(invoice.generated_at),
    };
  }

  public parseBackendModel(invoice: InvoiceBackendModel): InvoiceModel {
    return {
      id: invoice.id,
      type: invoice.type,
      invoiceNr: invoice.invoice_nr,
      bookingId: invoice.id_booking,
      bookingNr: invoice.booking_nr,
      facility: invoice.facility,
      firstName: invoice.first_name,
      lastName: invoice.last_name,
      email: invoice.email,
      phoneNumber: invoice.phone_number,
      street: invoice.street,
      streetNr: invoice.street_nr,
      zip: invoice.zip,
      city: invoice.city,
      country: invoice.country,
      totalAmount: invoice.total_amount && Number(invoice.total_amount),
      discountedAmount:
        invoice.discounted_amount && Number(invoice.discounted_amount),
      generatedAt: invoice.generated_at,
      paymentMethod: invoice.payment_method,
      paymentStatus: invoice.payment_status,
      coupons: invoice.coupons?.map(coupon =>
        this.couponService.parseCouponCodeBackendModel(coupon)
      ),
      creator:
        invoice.creator &&
        this.userService.parseBackendListModel(invoice.creator),
      seminar: invoice.seminar && {
        id: invoice.seminar.id,
        title: invoice.seminar.title,
        seminarNr: invoice.seminar.seminar_nr,
      },
      positions:
        invoice.positions &&
        invoice.positions.map(position => ({
          id: position.id,
          position: position.position,
          title: position.title,
          quantity: position.quantity,
          price: position.price,
          total: position.total,
        })),
    };
  }
}
